import {nanoid, Paths} from "@2d-front-monorepo/core";
import {
	Context,
	createContext,
	JSX,
	ReactNode,
	RefObject,
	useContext,
	useEffect,
	useImperativeHandle,
	useLayoutEffect,
	useMemo,
	useReducer,
	useRef
} from "react";

import {
	IMvvmContextState, MvvmContextChangeMethod,
	MvvmContextInitialState,
	MvvmContextState,
	MvvmContextUnsubscribe,
	MvvmContextValidationMethod
} from "./MvvmContextState";
import {unwrapMvvmContext, useMvvmTrackingFacade} from "./useMvvmTracking";
import {SyntheticChannel} from "./MvvmTrackingProxy";

export const MvvmContext: Context<MvvmDataProviderRef<any> | undefined> = createContext<MvvmDataProviderRef<any> | undefined>(undefined);

export interface MvvmDataProviderRef<T extends object> {
	context: IMvvmContextState<T>;
}

export interface MvvmDataProviderProps<T extends object> {
	context: IMvvmContextState<T>;
	onSubmit: (data: T) => Promise<void>;
	children: ReactNode;
	className?: string;
	ref?: RefObject<HTMLFormElement | null>;
}

export function MvvmDataProvider<T extends object>(props: MvvmDataProviderProps<T>): JSX.Element {
	const {onSubmit, className, ref, children} = props;
	// Hosts pass `handle.context`, which is a tracking facade — store the raw context so
	// provider wiring and useMvvmContext consumers (which build their own facade) don't
	// stack facades or record reads onto the host.
	const context = unwrapMvvmContext(props.context);
	const id = useMemo(() => `form-${nanoid()}`, []);

	const upperContext = useContext(MvvmContext)?.context as MvvmContextState<T>;
	useEffect(() => {
		// A provider nested inside one for the same context (a sub-form wrapping itself to
		// get useMvvmContext) must not register the context as its own child: every
		// aggregate read (isInvalid, isSubmitting, ...) recurses into children forever.
		if (upperContext == null || upperContext === context) {
			return undefined;
		}

		(upperContext as MvvmContextState<T>).registerChildren(context as MvvmContextState<any>);
		return () => (upperContext as MvvmContextState<T>).unregisterChildren(context as MvvmContextState<any>);
	}, [upperContext, context]);

	const contextValue = useMemo(() => ({context}), [context]);

	// Re-render the provider only when submit state flips, so the `inert` prop on <form>
	// stays in sync. The provider intentionally does NOT subscribe to data paths —
	// children handle their own reactivity through useMvvmContext / useMvvmFieldSubscription.
	const isSubmitting = useMvvmChannelValue(context, SyntheticChannel.submitting, () => (context as MvvmContextState<T>).isSubmitting());

	const handleSubmit = useMemo(function () {
		return async function (e: React.SubmitEvent) {
			e.preventDefault();
			await (context as MvvmContextState<T>).handleSubmit(onSubmit);
		};
	}, [context, onSubmit]);

	if (upperContext == null) {
		return <MvvmContext.Provider value={contextValue}>
			<form className={className}
			      noValidate={true}
			      id={id}
			      method="post"
			      inert={isSubmitting}
			      ref={ref}
			      onSubmit={handleSubmit}>
				{children}
			</form>
		</MvvmContext.Provider>;
	}

	return <MvvmContext.Provider value={contextValue}>{children}</MvvmContext.Provider>;
}

export interface MvvmContextProps<T extends object> {
	initialState: MvvmContextInitialState<T>;
	initialize?: (ctx: IMvvmContextState<T>) => Promise<void>;
	contextRef?: RefObject<MvvmDataProviderRef<T>>;
	onChange?: MvvmContextChangeMethod<T>;
	validation?: MvvmContextValidationMethod<T>;
	submitValidation?: MvvmContextValidationMethod<T>;
	validateOnMount?: boolean;
}

export type MvvmContextHandle<T extends object> =
	| { context: IMvvmContextState<T>; status: "loading"; error: undefined }
	| { context: IMvvmContextState<T>; status: "ready"; error: undefined }
	| { context: IMvvmContextState<T>; status: "error"; error: Error };

export function useInitializeMvvmContext<T extends object>(props: MvvmContextProps<T>, deps?: readonly unknown[]): MvvmContextHandle<T> {
	const {initialState, initialize, contextRef, onChange, validation, submitValidation, validateOnMount} = props;

	const [, forceRender] = useReducer(c => c + 1, 0);
	const prevDepsRef = useRef<readonly unknown[] | undefined>(undefined);

	const stateRef = useRef<MvvmContextState<T>>(undefined);

	const depsChanged = deps !== undefined &&
		(prevDepsRef.current === undefined ||
			deps.length !== prevDepsRef.current.length ||
			deps.some((dep, index) => dep !== prevDepsRef.current![index]));

	if (stateRef.current == null || depsChanged) {
		if (stateRef.current != null) {
			stateRef.current.dispose();
		}

		stateRef.current = new MvvmContextState<T>(initialState);
		prevDepsRef.current = deps;

		if (initialize != null) {
			stateRef.current.runInitialization(initialize);
		}
	}

	const context = stateRef.current;

	// Only the init lifecycle is subscribed unconditionally: the handle's `status`/`error`
	// are plain values read here, not through the facade. Everything else the host renders
	// (state paths, isSubmitting(), getErrors(), ...) is read through the tracking facade
	// below, which subscribes exactly what was read. Subscribing $validating/$errors/... for
	// every host cost two host re-renders per validation pass whether or not it showed them.
	// Layout effect so the subscription exists before any child's passive mount effect
	// writes (see useMvvmTrackingFacade).
	useLayoutEffect(() => context.subscribe(SyntheticChannel.initStatus, forceRender), [context]);

	useImperativeHandle(contextRef, () => ({
		context
	}), [context]);

	context.setOnChange(onChange);
	context.setValidation(validation);
	context.setSubmitValidation(submitValidation);

	const initStatus = context.initStatus;
	useEffect(() => {
		if (validateOnMount && initStatus === "ready") context.triggerValidation();
	}, [validateOnMount, context, initStatus]);

	// React 18 StrictMode-safe disposal:
	// On real unmounting we want context.dispose(); on StrictMode dev-mode mount/unmount/mount
	// we want to keep the same context alive. The trick is to mark ownership via a fresh
	// identity per mount and check it in a microtask: a real unmounting leaves the identity
	// untouched (cleanup runs), while StrictMode's immediate remount replaces it before
	// the microtask fires, so we skip disposal.
	const ownershipRef = useRef<object | undefined>(undefined);
	useEffect(() => {
		const id = ownershipRef.current = {};
		return () => {
			queueMicrotask(() => {
				if (ownershipRef.current === id) {
					context.dispose();
				}
			});
		};
	}, [context]);

	// The handle exposes a tracking facade: any render-time read (`handle.context.state.x`,
	// getValue, getError, isSubmitting, ...) auto-subscribes the host to exactly what it
	// read — in the host itself or in a descendant the facade was passed to as a prop
	// (descendants re-render via the host's cascade). Callback/effect reads work too: state
	// mutates in place, so they always see the latest value. Mvvm controls unwrap back to
	// the raw context so their per-field reads don't accumulate on the host.
	const facade = useMvvmTrackingFacade(context);

	return {context: facade, status: context.initStatus, error: context.initError} as MvvmContextHandle<T>;
}

export function useMvvmContext<T extends object>(): IMvvmContextState<T> {
	const handle = useContext(MvvmContext);
	if (handle === undefined) {
		throw new Error("useMvvmContext must be used within a MvvmDataProvider");
	}

	return useMvvmTrackingFacade(handle.context as MvvmContextState<T>);
}

/**
 * Returns a tracked proxy over `context.state`. Reads on the proxy auto-subscribe the
 * calling component to the specific paths touched (leaf-precision), so the component
 * re-renders only when one of those paths changes. Mutations still belong in
 * `context.update(...)` or `context.setValue(...)`.
 *
 * `handle.context.state.x` reads are tracked the same way through the handle's own facade;
 * this hook exists for components that receive the context as a prop and want their OWN
 * precise subscriptions instead of re-rendering through the host's cascade.
 */
export function useMvvmTrackedState<T extends object>(context: IMvvmContextState<T>): T {
	return useMvvmTrackingFacade(unwrapMvvmContext(context)).state;
}

/**
 * Subscribes the calling component to a single field path on the context. Used by
 * MvvmControls so they re-render when their bound path (or its error) changes,
 * regardless of whether the parent re-renders.
 *
 * The host re-renders on the root subscription already; this hook layers a precise
 * per-field subscription on top so React.memo'd controls can skip parent re-renders
 * and still react to their own data.
 */
export function useMvvmFieldSubscription<T extends object>(
	context: IMvvmContextState<T>,
	path: Paths<T> | undefined,
	options?: { subscribeError?: boolean, subscribeValue?: boolean }
): void {
	const [, forceRender] = useReducer(c => c + 1, 0);
	const subscribeError = options?.subscribeError ?? true;
	// Off for callers that only read getError(path): the value channel fires on every keystroke
	// and would re-render them for a value they never look at.
	const subscribeValue = options?.subscribeValue ?? true;
	// Layout effect: a control's own mount effect may write its path (MvvmSelect
	// auto-select-first, MvvmSelectMultiple [] normalisation) and must observe that write.
	useLayoutEffect(() => {
		if (path == null) return;
		const ctx = unwrapMvvmContext(context);
		const unsubs: MvvmContextUnsubscribe[] = [];
		if (subscribeValue) unsubs.push(ctx.subscribe(path, forceRender));
		if (subscribeError) unsubs.push(ctx.subscribe(SyntheticChannel.errorForPath(path), forceRender));
		return () => {
			for (const u of unsubs) {
				u();
			}
		};
	}, [context, path, subscribeError, subscribeValue]);
}

// Renders `getValue` once per render and re-renders the caller whenever the channel fires.
// Used by MvvmDataProvider for the form's `inert` prop without paying for a root subscription.
function useMvvmChannelValue<T>(context: IMvvmContextState<any>, channel: string, getValue: () => T): T {
	const [, forceRender] = useReducer(c => c + 1, 0);
	useLayoutEffect(() => {
		const ctx = unwrapMvvmContext(context);
		return ctx.subscribe(channel, forceRender);
	}, [context, channel]);
	return getValue();
}
